import type { RemoteBarcodeHit } from '../domain/barcodeLookup';
import type { PlanEntitlementsRow } from '../../shell/feature-flags/domain/planEntitlements';
import { CatalogBarcodeDuplicateError, CatalogDomainError, CatalogSkuDuplicateError } from '../domain/catalogErrors';
import { buildProductDraftFromSuggestion } from '../infra/productCatalogSearch';
import { createProduct } from '../infra/productsRepo';
import { validateBarcode, validateSku } from '../domain/catalogValidation';
import { assertWithinProductPlanLimit } from './catalogPlanLimits';

export type { RemoteBarcodeHit };

export type ImportProductFromRemoteSuggestionInput = {
  remoteHit: RemoteBarcodeHit;
  price: number;
  /** Nombre final (puede editarse respecto a la sugerencia). */
  name: string;
  categoryId: string | null;
  planEnt?: PlanEntitlementsRow | null;
  performedByUserId?: string | null;
  /** Stock inicial en la sucursal activa (POS). Default 1 si no se indica. */
  initialBranchQuantity?: number;
};

export class ImportProductFromRemoteValidationError extends CatalogDomainError {
  constructor(message: string) {
    super('IMPORT_PRODUCT_REMOTE_VALIDATION', message);
  }
}

export function validateImportProductFromRemoteSuggestionInput(input: {
  name: string;
  price: number;
  categoryId: string | null;
}): void {
  const trimmedName = input.name.trim();
  if (!trimmedName) {
    throw new ImportProductFromRemoteValidationError('Ingresá un nombre para el producto.');
  }

  if (!Number.isFinite(input.price) || input.price <= 0) {
    throw new ImportProductFromRemoteValidationError('Ingresá un precio mayor a cero.');
  }

  if (!input.categoryId?.trim()) {
    throw new ImportProductFromRemoteValidationError('Elegí una categoría para el producto.');
  }
}

export async function importProductFromRemoteSuggestion(
  input: ImportProductFromRemoteSuggestionInput
): Promise<{ productId: string }> {
  validateImportProductFromRemoteSuggestionInput(input);

  await assertWithinProductPlanLimit(input.planEnt ?? null);

  const draft = buildProductDraftFromSuggestion({
    barcode: input.remoteHit.barcode,
    suggestion: input.remoteHit.suggestion,
  });

  const sku = draft.sku.trim();
  const barcode = draft.barcode.trim();

  const skuMessage = validateSku(sku);
  if (skuMessage) {
    throw new ImportProductFromRemoteValidationError(skuMessage);
  }

  const barcodeMessage = validateBarcode(barcode);
  if (barcodeMessage) {
    throw new ImportProductFromRemoteValidationError(barcodeMessage);
  }

  const trimmedName = input.name.trim();

  try {
    const productId = await createProduct(
      {
        name: trimmedName,
        price: input.price,
        sku,
        barcode: barcode.length > 0 ? barcode : undefined,
        categoryId: input.categoryId!.trim(),
        itemType: 'physical',
      },
      {
        performedByUserId: input.performedByUserId ?? null,
        initialBranchQuantity: input.initialBranchQuantity ?? 1,
      }
    );
    return { productId };
  } catch (error) {
    if (error instanceof CatalogBarcodeDuplicateError) {
      throw new ImportProductFromRemoteValidationError(
        'Ese código ya existe en tu catálogo. Buscalo en la lista o revisá Catálogo.'
      );
    }
    if (error instanceof CatalogSkuDuplicateError) {
      throw new ImportProductFromRemoteValidationError('Ese SKU ya existe en tu catálogo.');
    }
    throw error;
  }
}
